import { Component, ViewChild, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NgForm } from '@angular/forms';
import { finalize, map, distinctUntilChanged, takeUntil } from 'rxjs';
import { BaseTableComponent } from '../../shared/base/base-table.component';
import {
  UserAdmin,
  UserAdminFilter,
  OfficeOption,
  UpdateUserRequest
} from '../../models/user-administrators.model';
import { UserAdministratorsService } from '../../services/user-administrators.service';
import { ClientSelectionService } from '../clients/client-selection.service';
import { formatHttpErrorMessage } from '../../shared/utils/error.utils';

interface DialogModel {
  orgUserName: string;
  userName: string;
  firstName: string;
  lastName: string;
  email: string;
  locked: boolean;
  approved: boolean;
  newUser: boolean;
  selectedOfficeIds: Set<number>;
}

@Component({
  selector: 'app-user-administrators',
  standalone: false,
  templateUrl: './user-administrators.component.html',
  styleUrls: ['./user-administrators.component.css']
})
export class UserAdministratorsComponent extends BaseTableComponent<UserAdmin> {

  override sortBy = 'userName';
  override sortDir: 'asc' | 'desc' = 'asc';

  readonly displayedColumns: string[] = [
    'userName', 'firstName', 'lastName', 'email', 'office', 'locked', 'approved', 'actions'
  ];

  filter: UserAdminFilter = { firstName: '', lastName: '', userName: '', email: '' };

  clientId?: number;
  offices: OfficeOption[] = [];
  officesLoaded = false;
  selectAllOffices = false;
  dialogError = '';
  showLockedCheckbox = false;

  model: DialogModel = this.emptyModel();

  @ViewChild('userDialog') dialogTemplate!: TemplateRef<any>;
  private dialogRef?: MatDialogRef<any>;

  constructor(
    private userAdminService: UserAdministratorsService,
    private clientSelection: ClientSelectionService,
    private dialog: MatDialog
  ) {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.resolveClientId();
  }

  protected override loadData(skip: number, count: number): void {
    if (!this.clientId) return;
    this.loading = true;

    this.userAdminService
      .getUsers(this.clientId, skip, count, this.sortBy, this.sortDir, this.filter)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: response => {
          this.dataSource.data = response.users;
          this.totalCount = response.totalCount;
          this.errorMessage = '';
        },
        error: err => {
          this.errorMessage = formatHttpErrorMessage('Failed to load users', err);
          this.dataSource.data = [];
          this.totalCount = 0;
        }
      });
  }

  onFilter(): void {
    this.pageIndex = 0;
    this.loadDataWrapper();
  }

  openEdit(row: UserAdmin): void {
    this.dialogError = '';
    this.errorMessage = '';
    this.showLockedCheckbox = row.locked;
    this.model = {
      orgUserName: row.userName,
      userName: row.userName,
      firstName: row.firstName,
      lastName: row.lastName,
      email: row.email,
      locked: row.locked,
      approved: row.approved,
      newUser: row.newUser,
      selectedOfficeIds: new Set(row.officeIds)
    };
    this.ensureOfficesLoaded(() => {
      this.updateSelectAll();
      this.dialogRef = this.dialog.open(this.dialogTemplate, {
        width: '580px',
        autoFocus: false,
        restoreFocus: false
      });
    });
  }

  closeDialog(): void {
    this.dialogRef?.close();
    this.dialogRef = undefined;
    this.showLockedCheckbox = false;
  }

  save(form: NgForm): void {
    form.control.markAllAsTouched();
    if (form.invalid) return;
    if (this.model.selectedOfficeIds.size === 0) {
      this.dialogError = 'At least one office must be selected.';
      return;
    }
    if (!this.clientId) return;

    this.dialogError = '';
    const request: UpdateUserRequest = {
      clientId: this.clientId,
      userName: this.model.userName,
      offices: Array.from(this.model.selectedOfficeIds),
      orgUserName: this.model.orgUserName,
      firstName: this.model.firstName,
      lastName: this.model.lastName,
      email: this.model.email,
      approved: this.model.approved,
      locked: this.model.locked
    };

    this.userAdminService.updateUser(request).subscribe({
      next: () => {
        this.loadDataWrapper();
        this.closeDialog();
      },
      error: err => {
        this.dialogError = formatHttpErrorMessage('Failed to update user', err);
      }
    });
  }

  toggleOffice(officeId: number, checked: boolean): void {
    if (checked) {
      this.model.selectedOfficeIds.add(officeId);
    } else {
      this.model.selectedOfficeIds.delete(officeId);
    }
    this.updateSelectAll();
  }

  onToggleSelectAll(checked: boolean): void {
    this.selectAllOffices = checked;
    this.model.selectedOfficeIds = checked
      ? new Set(this.offices.map(o => o.claimOfficeId))
      : new Set();
  }

  isOfficeSelected(officeId: number): boolean {
    return this.model.selectedOfficeIds.has(officeId);
  }

  private updateSelectAll(): void {
    this.selectAllOffices =
      this.offices.length > 0 &&
      this.offices.every(o => this.model.selectedOfficeIds.has(o.claimOfficeId));
  }

  private ensureOfficesLoaded(callback: () => void): void {
    if (this.officesLoaded) {
      callback();
      return;
    }
    this.userAdminService.getOffices().subscribe({
      next: offices => {
        this.offices = offices;
        this.officesLoaded = true;
        callback();
      },
      error: err => {
        this.errorMessage = formatHttpErrorMessage('Failed to load offices', err);
      }
    });
  }

  private resolveClientId(): void {
    this.clientSelection.selectedClient$
      .pipe(
        map(c => c?.ClientID ?? undefined),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(id => {
        this.clientId = id;
        this.officesLoaded = false;
        this.pageIndex = 0;
        if (id != null) {
          this.errorMessage = '';
          this.loadDataWrapper();
        } else {
          this.dataSource.data = [];
          this.totalCount = 0;
          this.errorMessage = 'Please select a client to view users.';
        }
      });
  }

  private emptyModel(): DialogModel {
    return {
      orgUserName: '',
      userName: '',
      firstName: '',
      lastName: '',
      email: '',
      locked: false,
      approved: false,
      newUser: false,
      selectedOfficeIds: new Set()
    };
  }
}

